// Helper central pra ordenar os nomes do casal de forma consistente em todo
// o app. O casal escolhe via `wedding.nameOrder` ('bride-first' | 'groom-first')
// nas configurações; default é 'bride-first' pra preservar páginas existentes
// (que historicamente já mostravam a noiva primeiro no hero).
//
// Use sempre que precisar mostrar "Ana & Pedro" / "Pedro & Ana" pra garantir
// que a ordem seja a mesma em todo lugar: hero, dashboard, sidebar, modais,
// emails, hashtag, monograma, share buttons, etc.
//
// `wedding.coupleConfig` ('bride-groom' | 'groom-groom' | 'bride-bride')
// controla só a ROTULAGEM (label, artigo, emoji, termo coletivo) exibida pro
// casal — os campos de dado continuam `groomName`/`brideName` por baixo,
// de propósito (ver "Schema do casal ainda gendered" em docs/BACKLOG.md).
// `groomName` sempre corresponde ao 1º campo preenchido no onboarding e
// `brideName` ao 2º, independente da config escolhida.

const DEFAULT_BRIDE_FALLBACK = 'Noiva';
const DEFAULT_GROOM_FALLBACK = 'Noivo';

const COUPLE_CONFIGS = ['bride-groom', 'groom-groom', 'bride-bride'];
const DEFAULT_COUPLE_CONFIG = 'bride-groom';

// Rótulo/artigo/emoji de cada um dos dois campos de dado (groom/bride) por
// config. O artigo é o que contrai com "de" nos labels ("Nome do Noivo",
// "Nome da Noiva").
const PARTNER_LABELS = {
  'bride-groom': {
    groom: { label: 'Noivo', article: 'o', emoji: '🤵' },
    bride: { label: 'Noiva', article: 'a', emoji: '👰' },
  },
  'groom-groom': {
    groom: { label: 'Noivo · 1', article: 'o', emoji: '🤵' },
    bride: { label: 'Noivo · 2', article: 'o', emoji: '🤵' },
  },
  'bride-bride': {
    groom: { label: 'Noiva · 1', article: 'a', emoji: '👰' },
    bride: { label: 'Noiva · 2', article: 'a', emoji: '👰' },
  },
};

// Termo coletivo usado em textos fixos ("Os Noivos", "fale com os noivos").
// "noivos" é literalmente o plural de "noivo", então vale pra noivo+noiva e
// noivo+noivo — só noiva+noiva precisa da forma feminina "as noivas".
const COLLECTIVE_TERM = {
  'bride-groom': { lower: 'os noivos', title: 'Os Noivos' },
  'groom-groom': { lower: 'os noivos', title: 'Os Noivos' },
  'bride-bride': { lower: 'as noivas', title: 'As Noivas' },
};

export function getCoupleConfig(wedding) {
  const config = typeof wedding === 'string' ? wedding : wedding?.coupleConfig;
  return COUPLE_CONFIGS.includes(config) ? config : DEFAULT_COUPLE_CONFIG;
}

// Aceita tanto um doc de wedding quanto a própria string de config — útil no
// Onboarding, onde ainda não existe um `wedding` salvo pra consultar.
export function getPartnerLabels(weddingOrConfig) {
  return PARTNER_LABELS[getCoupleConfig(weddingOrConfig)];
}

export function getCoupleCollectiveTerm(weddingOrConfig, { capitalize = false } = {}) {
  const term = COLLECTIVE_TERM[getCoupleConfig(weddingOrConfig)];
  return capitalize ? term.title : term.lower;
}

// "dos noivos" / "das noivas" — contração de "de" + o termo coletivo (que já
// vem com artigo: "os noivos"/"as noivas"). Usada em textos tipo "Ver página
// dos noivos" / "página das noivas", onde "página " + termo sozinho ficaria
// gramaticalmente errado ("página os noivos").
const COLLECTIVE_TERM_OF = {
  'bride-groom': 'dos noivos',
  'groom-groom': 'dos noivos',
  'bride-bride': 'das noivas',
};

export function getCoupleCollectiveTermOf(weddingOrConfig) {
  return COLLECTIVE_TERM_OF[getCoupleConfig(weddingOrConfig)];
}

// "Nome do Noivo", "Nome da Noiva · 2" etc. — junta o label com o artigo
// contraído certo ("do"/"da").
export function formatPartnerFieldLabel(partnerLabel, prefix = 'Nome') {
  const prep = partnerLabel.article === 'o' ? 'do' : 'da';
  return `${prefix} ${prep} ${partnerLabel.label}`;
}

// Partículas que ligam sobrenomes ("Maria da Silva", "João dos Santos") e não
// contam como sobrenome sozinhas.
const NAME_PARTICLES = new Set(['da', 'de', 'do', 'das', 'dos', 'e']);

// Reduz um nome completo a "Primeiro Sobrenome" (+ `extraSurnames` sobrenomes
// seguintes, usados pra desambiguar slug repetido). Partículas ficam coladas
// ao sobrenome que vem depois ("Ana da Silva"). Só afeta exibição/slug — o
// dado salvo continua o que o casal digitou.
export function shortName(fullName, extraSurnames = 0) {
  // Remove observações entre parênteses, ex.: "João Lucas (João Antônio Filho)".
  const words = String(fullName || '').replace(/\([^)]*\)?/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const out = [words[0]];
  let wanted = 1 + Math.max(0, extraSurnames);
  let i = 1;
  while (i < words.length && wanted > 0) {
    // Junta partículas ao sobrenome seguinte ("da Silva").
    const chunk = [];
    while (i < words.length && NAME_PARTICLES.has(words[i].toLowerCase()) && i < words.length - 1) {
      chunk.push(words[i]);
      i += 1;
    }
    if (i < words.length) {
      chunk.push(words[i]);
      i += 1;
    }
    out.push(...chunk);
    wanted -= 1;
  }
  return out.join(' ');
}

// Sufixo de slug "primeiro-sobrenome-e-primeiro-sobrenome". `attempt` > 0
// acrescenta o sobrenome seguinte de cada um (usado quando o slug já existe).
export function buildNameSlug(groomName, brideName, slugify, attempt = 0) {
  return slugify(`${shortName(groomName, attempt)}-e-${shortName(brideName, attempt)}`);
}

export function getCoupleNames(wedding, opts = {}) {
  const {
    brideFallback = DEFAULT_BRIDE_FALLBACK,
    groomFallback = DEFAULT_GROOM_FALLBACK,
  } = opts;

  const brideName = wedding?.brideName || brideFallback;
  const groomName = wedding?.groomName || groomFallback;
  const brideQuote = wedding?.brideQuote || '';
  const groomQuote = wedding?.groomQuote || '';
  const bridePhoto = wedding?.bridePhoto
    || (Array.isArray(wedding?.bridePhotos) ? wedding.bridePhotos[0] : '')
    || '';
  const groomPhoto = wedding?.groomPhoto
    || (Array.isArray(wedding?.groomPhotos) ? wedding.groomPhotos[0] : '')
    || '';

  const groomFirst = wedding?.nameOrder === 'groom-first';
  const partnerLabels = getPartnerLabels(wedding);
  const coupleConfig = getCoupleConfig(wedding);

  // Exibição usa "Primeiro Sobrenome"; brideName/groomName abaixo seguem
  // completos pra quem precisa do dado cru (ex.: Asaas).
  const shortBride = shortName(brideName);
  const shortGroom = shortName(groomName);

  return {
    firstName:  groomFirst ? shortGroom  : shortBride,
    secondName: groomFirst ? shortBride  : shortGroom,
    firstQuote: groomFirst ? groomQuote : brideQuote,
    secondQuote: groomFirst ? brideQuote : groomQuote,
    firstPhoto: groomFirst ? groomPhoto : bridePhoto,
    secondPhoto: groomFirst ? bridePhoto : groomPhoto,
    firstLabel: groomFirst ? partnerLabels.groom : partnerLabels.bride,
    secondLabel: groomFirst ? partnerLabels.bride : partnerLabels.groom,
    // Conveniências
    pair: `${groomFirst ? shortGroom : shortBride} & ${groomFirst ? shortBride : shortGroom}`,
    pairAnd: `${groomFirst ? shortGroom : shortBride} e ${groomFirst ? shortBride : shortGroom}`,
    // Acesso direto (independente da ordem) — útil quando precisa do nome
    // específico (ex.: enviar pra Asaas em campo "groomName").
    brideName,
    groomName,
    brideQuote,
    groomQuote,
    groomFirst,
    coupleConfig,
    collectiveTerm: getCoupleCollectiveTerm(wedding),
    collectiveTermTitle: getCoupleCollectiveTerm(wedding, { capitalize: true }),
  };
}

// Atalho pro caso comum de só precisar do par formatado.
export function coupleNamePair(wedding, separator = '&') {
  const { firstName, secondName } = getCoupleNames(wedding);
  return `${firstName} ${separator} ${secondName}`;
}
